import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useAnalyticsQuery } from "@/hooks/useAnalyticsQuery";
import { analyticsApi } from "@/services/apiClient";
import { MAX_RANGE_DAYS, daysAgoISODate, todayISODate } from "@/stores/filterStore";
import TrendTable from "@/components/TrendTable";
import RevenueCostChart from "@/components/RevenueCostChart";
import CostMatrix from "@/components/CostMatrix";
import ChargeHistory from "@/components/ChargeHistory";
import B2cEconomics from "@/components/B2cEconomics";
import SectionRangePicker from "@/components/SectionRangePicker";
import ExportButton from "@/components/ExportButton";
import CopyButton from "@/components/CopyButton";
import ToggleGroup from "@/components/ToggleGroup";
import InfoTooltip from "@/components/InfoTooltip";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import MobileStatusBanner, { worstLevel as bannerWorst } from "@/components/MobileStatusBanner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { KRW_PER_USD, rebucketSeries, cumulativeSeries, formatBucketLabel, totalExpenseKrw, netProfitKrw } from "@/lib/timeseries";
import { fmt } from "@/lib/format";
import Panel from "./Panel";
import Skeleton from "./Skeleton";

const GRANULARITY_OPTIONS = [
  { id: "day",   label: "일별" },
  { id: "week",  label: "주별" },
  { id: "month", label: "월별" },
];

const MODE_OPTIONS = [
  { id: "period",     label: "구간별" },
  { id: "cumulative", label: "누적" },
];

// 탭 내부 세그먼트(2026-07-18) — "추이(훑어보기)"와 "원장(건별로 찾아보기)"은 소비 방식이
// 달라서 한 세로줄에 쌓으면 펼치는 순간 스크롤이 무한정 길어진다. 접힘 드릴다운 대신
// KPI는 상단 고정 + 그 아래를 세그먼트로 나눠 어느 화면이든 한 화면 남짓으로 끝나게 함.
// (사용자 상세 패널의 도구별/모델별/이용 내역과 같은 패턴 — 학습 비용 0)
// 세그먼트 이름은 KPI 카드의 단어(지출/순이익)와 맞춘다(2026-07-18 피드백). 충전 내역만
// "매출 내역"이 아닌 이유: 수기 지급(매출 아님)도 그 화면에 있어서 "매출"을 붙이면 수기
// 지급까지 매출로 읽힐 위험이 있고, 사용자도 "충전 내역"이라는 단순한 이름을 원함.
const SEGMENTS = [
  { id: "trend",   label: "손익 추이" },
  { id: "charges", label: "충전 내역" },
  { id: "costs",   label: "지출 세부내역" },
];

function defaultRange() {
  return { from: daysAgoISODate(MAX_RANGE_DAYS - 1), to: todayISODate() };
}

function shiftISO(dateStr, days) {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// KPI 카드용 기간 합계 — 백엔드 일별 series 행을 그대로 합산한다. 활성 사용자는 일별 수를
// 더하면 같은 사람이 여러 날 활동한 만큼 중복되므로 id 합집합(union) 크기로 계산.
function sumPeriod(series) {
  let revenue = 0, expense = 0;
  const users = new Set();
  for (const r of series || []) {
    revenue += r.revenueKrw || 0;
    expense += totalExpenseKrw(r);
    (r.activeUserIds || []).forEach((u) => users.add(u));
  }
  return { revenue, expense, profit: revenue - expense, activeUsers: users.size };
}

// 실청구 API 의 일별 USD 를 series 행에 KRW 필드로 병합하는 공통 헬퍼 — OpenAI(openaiKrw)와
// BillingON AWS(awsKrw)가 응답 모양({days:[{date,usd}]})과 병합 방식이 완전히 같아서
// 한 함수로 묶었다(2026-07-27, BillingON 추가하며). 조회 실패 시 days=[] 로 들어와
// 그냥 수기 입력분만 집계된다(치명적이지 않음).
function mergeDailyUsd(series, days, field) {
  const byDate = new Map((days || []).map((d) => [d.date, (d.usd || 0) * KRW_PER_USD]));
  if (byDate.size === 0) return series || [];
  const seen = new Set();
  const out = (series || []).map((r) => {
    seen.add(r.bucket);
    return { ...r, [field]: byDate.get(r.bucket) || 0 };
  });
  // 그날 사용/결제 행이 전혀 없어 series 에서 빠진 날짜에 청구만 있는 경우도
  // 지출에 반영되도록 행을 만들어 붙인다.
  for (const [date, krw] of byDate) {
    if (!seen.has(date)) out.push({ bucket: date, [field]: krw });
  }
  return out;
}

// OpenAI Costs API + BillingON(AWS) 일별 실청구(USD)를 일별 series 행에 병합 — 수기 입력
// (expenseKrw)과 함께 "실지출" 기준 지출을 구성한다(2026-07-21, AWS 추가 2026-07-27).
function mergeRealCosts(series, openaiDays, awsDays) {
  return mergeDailyUsd(mergeDailyUsd(series, openaiDays, "openaiKrw"), awsDays, "awsKrw");
}

// 직전 기간 대비 증감률 — 직전 값이 0이면 비율이 무의미해서 null(배지 생략).
function deltaPct(cur, prev) {
  if (!Number.isFinite(prev) || prev === 0) return null;
  return (cur - prev) / Math.abs(prev);
}

// "도구별 사용량 추이"는 별도 탭(ToolUsageSection)으로 분리(2026-07-16) — 이 탭은 이제
// NC 충전/사용·마진·캐시히트·활성 사용자 등 "서비스 전체의 매출·비용 건강도" 시계열만 다룬다.
export default function TrendSection() {
  // 기본 주별(2026-07-21 피드백) — 손익은 일 단위 변동이 커서 주 단위가 추세 읽기에 맞다.
  const [granularity, setGranularity] = useState("week");
  const isMobile = useIsMobile();
  const [mode, setMode] = useState("period"); // "period" | "cumulative" — 도구별 사용량 탭과 동일 UI 언어
  const [trendRange, setTrendRange] = useState(defaultRange);
  const [segment, setSegment] = useState("trend");
  const [tableOpen, setTableOpen] = useState(false); // 차트 아래 기간별 표 접기/펴기 (기본 접힘)

  // byModelCost 는 더 안 붙인다 — 비용 세부내역(CostMatrix)이 독립 기간 피커를 갖게 되면서
  // 펼칠 때 스스로 조회한다(2026-07-18). 이 메인 쿼리는 차트/표/KPI용으로 가볍게 유지.
  const { data: ts, loading: tsLoading, error: tsError } = useAnalyticsQuery(
    (p, s) => analyticsApi.timeseries({ ...p, granularity: "day", scope: "customer" }, s),
    [trendRange.from, trendRange.to],
    trendRange
  );
  // KPI 카드의 "직전 기간 대비" 배지용 — 선택 범위와 같은 길이의 직전 구간을 별도로
  // 가볍게(byModelCost 없이) 한 번 더 조회한다. 범위를 2배로 늘려 한 번에 받아오는 방법은
  // 백엔드의 MAX_RANGE_DAYS(90일) 상한에 걸려서 못 쓴다.
  const rangeLenDays = Math.round((new Date(trendRange.to) - new Date(trendRange.from)) / 86400000) + 1;
  const prevRange = { from: shiftISO(trendRange.from, -rangeLenDays), to: shiftISO(trendRange.from, -1) };
  const { data: prevTs } = useAnalyticsQuery(
    (p, s) => analyticsApi.timeseries({ ...p, granularity: "day", scope: "customer" }, s),
    [prevRange.from, prevRange.to],
    prevRange
  );
  // OpenAI 실청구(자동) — 지출의 한 축. 키 미설정 등 실패해도 추이는 계속 그린다.
  const { data: oa } = useAnalyticsQuery(
    (p, s) => analyticsApi.openaiCosts.list(p, s).catch(() => ({ days: [] })),
    [trendRange.from, trendRange.to],
    trendRange
  );
  const { data: prevOa } = useAnalyticsQuery(
    (p, s) => analyticsApi.openaiCosts.list(p, s).catch(() => ({ days: [] })),
    [prevRange.from, prevRange.to],
    prevRange
  );
  // AWS 실청구(자동, BillingON) — 지출의 가장 큰 축. Cost Explorer 가 조직 SCP 로 막혀
  // 있어서 BillingON SaaS API 를 프록시한다(2026-07-27). 시크릿 미설정·WAF 차단 등
  // 실패해도 추이는 계속 그린다(그 경우 수기 입력분만 반영).
  const { data: bo } = useAnalyticsQuery(
    (p, s) => analyticsApi.billingonCosts.list(p, s).catch(() => ({ days: [] })),
    [trendRange.from, trendRange.to],
    trendRange
  );
  const { data: prevBo } = useAnalyticsQuery(
    (p, s) => analyticsApi.billingonCosts.list(p, s).catch(() => ({ days: [] })),
    [prevRange.from, prevRange.to],
    prevRange
  );
  const dailySeries = mergeRealCosts(ts?.series, oa?.days, bo?.days);
  const bucketed = rebucketSeries(dailySeries, granularity, trendRange);
  const series = (mode === "cumulative" ? cumulativeSeries(bucketed) : bucketed)
    .map((r) => ({ ...r, bucketLabel: formatBucketLabel(r.bucket, granularity) }));
  const cur = sumPeriod(dailySeries);
  const prev = prevTs ? sumPeriod(mergeRealCosts(prevTs.series, prevOa?.days, prevBo?.days)) : null;
  const csvRows = [
    ["기간", "매출(KRW)", "지출(KRW)", "직접비 기여이익(KRW)", "기여이익률%", "캐시히트%"],
    ...series.map((r) => [
      r.bucketLabel || r.bucket, r.revenueKrw, Math.round(totalExpenseKrw(r)), Math.round(netProfitKrw(r)),
      fmt.pct(r.marginPct), fmt.pct(r.cacheHitRate),
    ]),
  ];

  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-1.5">
          매출·마진 추이
          <InfoTooltip
            intro="핵심은 직접비 기여이익(매출-추적된 직접비)입니다. 지출은 실청구 기준입니다. PG 수수료·VAT·인건비·마케팅비는 포함되지 않아 회계상 순이익이 아닙니다."
            items={[
              { term: "매출", desc: "실제 결제된 토스 금액(환불 제외)" },
              { term: "지출", desc: "AWS 실청구(BillingON 자동) + OpenAI 실지출(자동) + 수기 입력(인건비·구독료 등) — 토큰 추정치 아님. AWS 는 같은 날 수기 입력이 있으면 수기 우선" },
              { term: "최근 1~2일", desc: "AWS 청구 데이터가 지연 확정돼 어제·오늘은 0 또는 부분값 — 그만큼 지출이 과소, 기여이익이 과대 표시된다" },
              { term: "건별 상세", desc: "\"충전 내역\"·\"지출 세부내역\" 화면에서" },
              { term: "차트 범례", desc: "클릭하면 그 라인을 켜고 끌 수 있음" },
              { term: "기여이익률%", desc: "직접비 기여이익 ÷ 매출. 회계상 순이익률이 아니다 — PG 수수료·VAT·인건비 등이 빠져 있다" },
              { term: "캐시히트%", desc: "AI 사용 효율 참고 지표 — 손익과 별개" },
            ]}
          />
        </span>
      }
      action={
        <SectionRangePicker from={trendRange.from} to={trendRange.to} onChange={(from, to) => setTrendRange({ from, to })} />
      }
    >
      {tsError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          데이터를 불러오지 못했습니다 — {tsError.message} ({tsError.code}). 위 날짜 범위가 올바른지 확인해 주세요.
        </div>
      )}
      {/* 최초 로드일 때만 스켈레톤 — 기간만 바꾼 재조회는 기존 표를 유지(2026-07-15). */}
      {!tsError && (tsLoading && !ts ? <Skeleton h={72} variant="chartTable" /> : (
        <div className="animate-fade-in">
          {/* 모바일 상태 배너 — 손익 도메인 언어(적자·매출 감소). deterministic(cur/prev)만 사용. */}
          {isMobile && (() => {
            const alerts = [];
            if (cur.profit < 0) alerts.push({ level: "warn", message: "이 기간 직접비 기여이익이 적자입니다." });
            if (cur.expense > cur.revenue) alerts.push({ level: "warn", message: "이 기간 지출이 매출을 초과했습니다." });
            if (prev && prev.revenue > 0 && cur.revenue < prev.revenue * 0.75)
              alerts.push({ level: "info", message: "매출이 직전 동일 기간 대비 감소했습니다." });
            const level = bannerWorst(alerts);
            return <MobileStatusBanner domain="revenue" level={level} messages={alerts.map((a) => a.message)} className="mb-4" />;
          })()}
          {/* KPI 순서는 전 탭 공통 규칙(2026-07-18): 핵심(블루) → 긍정(민트) → 참고(중립)
              → 주의(앰버). 순이익이 3번째라 이 탭만 어긋나 보인다는 피드백 — 결론(순이익)
              먼저로 재배치. 모바일은 hero(순이익 full-width) + 375px+ 2열 + 축약 통화. */}
          <KpiRow mobileLayout="hero">
            <KpiCard label="직접비 기여이익" value={isMobile ? fmt.krwCompact(cur.profit) : fmt.krw(cur.profit)} valueTitle={isMobile ? fmt.krw(cur.profit) : undefined} tone="brand" hero mobileHero deltaBelow={isMobile}
              deltaPct={prev ? deltaPct(cur.profit, prev.profit) : null} sublabel="매출 − 지출" />
            <KpiCard label="매출" value={isMobile ? fmt.krwCompact(cur.revenue) : fmt.krw(cur.revenue)} valueTitle={isMobile ? fmt.krw(cur.revenue) : undefined} tone="good" deltaBelow={isMobile}
              deltaPct={prev ? deltaPct(cur.revenue, prev.revenue) : null} sublabel="이 기간 실제 결제 금액" />
            <KpiCard label="지출" value={isMobile ? fmt.krwCompact(cur.expense) : fmt.krw(cur.expense)} valueTitle={isMobile ? fmt.krw(cur.expense) : undefined} tone="warn" invert deltaBelow={isMobile}
              deltaPct={prev ? deltaPct(cur.expense, prev.expense) : null} sublabel="실청구 기준 (AWS·OpenAI 자동 + 수기)" />
            <KpiCard label="활성 사용자" value={fmt.int(cur.activeUsers)}
              deltaPct={prev ? deltaPct(cur.activeUsers, prev.activeUsers) : null} sublabel="이 기간 활동한 순 인원" />
          </KpiRow>
          <div className="mb-4 inline-flex rounded-lg border border-ink-200 bg-white p-1 shadow-sm">
            {SEGMENTS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSegment(s.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  segment === s.id ? "bg-brand-600 text-white shadow-sm" : "text-ink-500 hover:bg-ink-100 hover:text-ink-800"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {/* 비활성 세그먼트는 unmount 대신 hidden — 다시 돌아왔을 때 받아둔 데이터·필터가 유지되게. */}
          <div className={segment === "trend" ? "" : "hidden"}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">직접비 기여이익·매출액·지출액 추이</h3>
              <div className="flex flex-wrap items-center gap-2">
                <ToggleGroup options={GRANULARITY_OPTIONS} value={granularity} onChange={setGranularity} />
                <ToggleGroup options={MODE_OPTIONS} value={mode} onChange={setMode} />
                <CopyButton rows={csvRows} />
                <ExportButton filename="trend" rows={csvRows} />
              </div>
            </div>
            <RevenueCostChart series={series} />
            {/* 차트 아래 기간별 표는 통째로 접었다 편다(2026-08-05 피드백) — 추세는 차트로
                충분할 때가 많고, 숫자를 확인할 때만 표를 펼치면 화면이 짧아진다. */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setTableOpen((v) => !v)}
                aria-expanded={tableOpen}
                title={tableOpen ? "기간별 표 접기" : "기간별 표 펴기"}
                className="flex w-full items-center justify-between rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-600 hover:bg-ink-50"
              >
                <span>기간별 표 {tableOpen ? "접기" : "펴기"}</span>
                {tableOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {tableOpen && (
                <div className="mt-2">
                  <TrendTable series={series} cumulative={mode === "cumulative"} />
                </div>
              )}
            </div>
            {/* A-4 — 접힌 상태로 두고 펼칠 때만 ?segments=1 을 따로 부른다. 기존 overview
                호출과 초기 로딩 성능은 그대로다(B2cEconomics 주석 참고). */}
            <B2cEconomics range={trendRange} />
          </div>
          <ChargeHistory range={trendRange} active={segment === "charges"} />
          <CostMatrix range={trendRange} active={segment === "costs"} />
        </div>
      ))}
    </Panel>
  );
}
